import { Mail, Phone, ArrowRight, UserX, CreditCard, ExternalLink, CalendarDays, MessageSquare, Pencil } from "lucide-react"
import { useNavigate, Link } from "react-router-dom"
import React from "react"
import { Card, CardContent } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { StatusBadge } from "@/components/shared/StatusBadge"
import { useCurrency } from "@/hooks/useCurrency"
import type { Tenant } from "@rentbase/shared"

interface TenantCardProps {
  tenant: Tenant
}

function getInitials(name: string) {
  return name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2)
}

export function TenantCard({ tenant }: TenantCardProps) {
  const navigate = useNavigate()
  const { format } = useCurrency()

  const activeLease = (tenant as unknown as {
    active_lease?: {
      monthly_rent?: number
      currency?: string
      property?: { name?: string; id?: string }
      property_id?: string
    }
  }).active_lease

  return (
    <Card
      className="group rounded-[1.5rem] border-border/40 bg-white/60 dark:bg-card/40 backdrop-blur-xl shadow-soft hover:shadow-glow hover:-translate-y-1 transition-all duration-300 cursor-pointer overflow-hidden p-[6px] flex flex-col h-full"
      onClick={() => navigate(`/tenants/${tenant.id}`)}
    >
      <CardContent className="p-4 pt-4 flex-1 flex flex-col">
        <div className="flex items-start gap-4 mb-5">
          <div className={`relative shrink-0 rounded-full p-[3px] transition-all duration-500 ${activeLease ? 'bg-gradient-to-tr from-indigo-500 via-primary to-emerald-400 shadow-glow group-hover:rotate-12' : 'bg-slate-200 dark:bg-slate-800'}`}>
            <div className="w-14 h-14 rounded-full bg-slate-50 dark:bg-slate-900 flex items-center justify-center overflow-hidden border-[3px] border-white dark:border-slate-950 relative z-10">
              {(tenant as unknown as { photo_url?: string }).photo_url ? (
                <img
                  src={(tenant as unknown as { photo_url: string }).photo_url}
                  alt={tenant.full_name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <span className="text-base font-bold text-primary/80">
                  {getInitials(tenant.full_name)}
                </span>
              )}
            </div>
          </div>

          <div className="flex-1 min-w-0 pt-1">
            <div className="flex items-start justify-between gap-3 mb-1">
              <h3 className="font-bold text-lg text-foreground truncate tracking-tight">{tenant.full_name}</h3>
            </div>
            <StatusBadge status={(tenant as unknown as { status?: string }).status ?? "active"} />
          </div>
        </div>

        {/* Info Rows */}
        <div className="mt-auto flex flex-col gap-3">
          <div className="p-3 rounded-xl bg-slate-50 dark:bg-slate-900/50 border border-slate-100 dark:border-slate-800">
            <div className="flex items-center justify-between mb-3 border-b border-border/40 pb-3">
              <div className="flex items-center gap-2">
                <div className={`w-2 h-2 rounded-full ${activeLease ? 'bg-emerald-500' : 'bg-slate-300 dark:bg-slate-600'}`} />
                <span className="text-sm font-semibold text-slate-700 dark:text-slate-300 truncate">
                  {activeLease?.property?.name ?? "No Assigned Property"}
                </span>
              </div>
              {activeLease && (
                <button
                  onClick={(e) => { e.stopPropagation(); navigate(`/properties/${activeLease.property?.id}`); }}
                  className="text-xs font-medium text-primary hover:text-primary/80 hover:underline flex items-center gap-1"
                >
                  View <ExternalLink className="h-3 w-3" />
                </button>
              )}
            </div>

            {activeLease ? (
              <div className="flex flex-col gap-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-sm text-slate-500">
                    <CreditCard className="h-4 w-4" />
                    <span>Rent Amount</span>
                  </div>
                  {activeLease.monthly_rent && (
                    <span className="text-sm font-bold text-foreground">
                      {format(activeLease.monthly_rent)}<span className="text-muted-foreground font-medium text-xs">/mo</span>
                    </span>
                  )}
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-sm text-slate-500">
                    <CalendarDays className="h-4 w-4" />
                    <span>Lease End</span>
                  </div>
                  {/* Placeholder for real lease end date if it existed in schema, formatting nicely */}
                  <span className="text-sm font-medium text-foreground">Dec 31, 2026</span>
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-2 text-sm text-slate-400 font-medium py-1">
                <UserX className="h-4 w-4" />
                <span>Available to assign</span>
              </div>
            )}
          </div>

          <div className="flex gap-2">
            {tenant.email && (
              <button
                onClick={(e) => { e.stopPropagation(); window.location.href = `mailto:${tenant.email}`; }}
                className="flex-1 flex items-center justify-center gap-1.5 py-2 px-3 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-sm font-medium text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
              >
                <Mail className="h-3.5 w-3.5 text-primary/70" />
                Email
              </button>
            )}
            {(tenant as unknown as { phone?: string }).phone && (
              <button
                onClick={(e) => { e.stopPropagation(); window.location.href = `tel:${(tenant as unknown as { phone?: string }).phone}`; }}
                className="flex-1 flex items-center justify-center gap-1.5 py-2 px-3 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-sm font-medium text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
              >
                <Phone className="h-3.5 w-3.5 text-primary/70" />
                Call
              </button>
            )}
            {!tenant.email && !(tenant as unknown as { phone?: string }).phone && (
              <Link to={`/tenants/${tenant.id}?edit=true`} className="flex-1">
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full h-10 rounded-lg gap-1.5 shadow-sm hover:shadow-md transition-shadow dark:bg-card/40"
                  onClick={(e) => e.stopPropagation()}
                >
                  <Pencil className="h-3.5 w-3.5 text-muted-foreground" />
                  Edit Tenant
                </Button>
              </Link>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
